import { STORAGE_KEY } from './constants'
import type { PlannerStorage } from '../types'

const DEFAULTS: PlannerStorage = {
  accessToken: null,
  refreshToken: null,
  tokenExpiresAt: null,
  pairedAt: null,
  pairedOrg: null,
  pairedUser: null,
}

// ── Office.roamingSettings wrapper ────────────────────────────────────────────
// roamingSettings persists per-mailbox, synced across Outlook clients.
// Falls back to localStorage in environments where Office.js isn't loaded (tests/dev).

function getRoamingSettings(): Office.RoamingSettings | null {
  try {
    return Office.context?.roamingSettings ?? null
  } catch {
    return null
  }
}

export function getStorage(): PlannerStorage {
  const rs = getRoamingSettings()

  if (rs) {
    const stored = rs.get(STORAGE_KEY) as PlannerStorage | undefined
    return { ...DEFAULTS, ...(stored ?? {}) }
  }

  // localStorage fallback (dev / non-Office environments)
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULTS }
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<PlannerStorage>) }
  } catch {
    return { ...DEFAULTS }
  }
}

export function setStorage(updates: Partial<PlannerStorage>): void {
  const current = getStorage()
  const next: PlannerStorage = { ...current, ...updates }

  const rs = getRoamingSettings()
  if (rs) {
    rs.set(STORAGE_KEY, next)
    rs.saveAsync()
    return
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable — continue silently
  }
}

// ── Token helpers ─────────────────────────────────────────────────────────────

export function setTokens(params: {
  accessToken: string
  refreshToken?: string
  tokenExpiresAt: number
  pairedOrg?: { id: string; name: string }
  pairedUser?: { role: string; nameFirst: string | null; nameLast: string | null }
}): void {
  const updates: Partial<PlannerStorage> = {
    accessToken: params.accessToken,
    tokenExpiresAt: params.tokenExpiresAt,
  }
  if (params.refreshToken !== undefined) updates.refreshToken = params.refreshToken
  if (params.pairedOrg !== undefined) updates.pairedOrg = params.pairedOrg
  if (params.pairedUser !== undefined) updates.pairedUser = params.pairedUser

  // Record pairing time on first pair (refreshToken present)
  if (params.refreshToken !== undefined && !getStorage().pairedAt) {
    updates.pairedAt = new Date().toISOString()
  }

  setStorage(updates)
}

export function clearTokens(): void {
  setStorage({
    accessToken: null,
    refreshToken: null,
    tokenExpiresAt: null,
    pairedAt: null,
    pairedOrg: null,
    pairedUser: null,
  })
}

export function isTokenExpired(tokenExpiresAt: number | null): boolean {
  if (!tokenExpiresAt) return true
  // Treat as expired 60 s early to avoid clock-skew races
  return Date.now() > tokenExpiresAt - 60_000
}

export function isAuthenticated(): boolean {
  const s = getStorage()
  if (s.accessToken && !isTokenExpired(s.tokenExpiresAt)) return true
  if (s.refreshToken) return true  // can still refresh
  return false
}
